const READ_METADATA_TIMEOUT_MS = 10_000;

/**
 * Reads a video file's duration (in seconds) by loading its metadata via a
 * temporary HTML5 <video> element. Resolves with the duration on success,
 * rejects if the browser cannot parse the file, duration is non-finite, or
 * the browser never fires loadedmetadata within the timeout.
 *
 * Exported as its own module so tests can mock it without touching jsdom's
 * incomplete <video> metadata pipeline.
 */
export async function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    let timeoutId = 0;

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      video.onloadedmetadata = null;
      video.onerror = null;
      fn();
    };

    timeoutId = window.setTimeout(
      () => settle(() => reject(new Error('Timed out reading video metadata'))),
      READ_METADATA_TIMEOUT_MS
    );

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const seconds = video.duration;
      if (!Number.isFinite(seconds) || seconds <= 0) {
        settle(() => reject(new Error('Could not determine video duration')));
        return;
      }
      settle(() => resolve(seconds));
    };
    video.onerror = () => {
      settle(() => reject(new Error('Failed to read video metadata')));
    };
    video.src = url;
  });
}
